import { amountUtils, type Except } from '@festina-life/core'
import { DialogPanel } from '@headlessui/react'
import cx from 'classnames'
import { memo, type RefObject, useEffect, useRef, useState } from 'react'
import Draggable, {
  type DraggableData,
  type DraggableEvent,
} from 'react-draggable'

import { useResizeObserver } from '../../hooks/useResizeObserver'
import { useResizeWindow } from '../../hooks/useResizeWindow'
import { paperStyles } from '../../libs/paperStyles'
import { ModalProvider } from './context/ModalProvider'
import type { ModalProps } from './types'

const MemorizedModalDraggableDialogPanel = ({
  isOpen,
  onClose,
  children,
  panelClassName,
  ...htmlProps
}: Except<ModalProps, 'className'>) => {
  const draggableRef = useRef(null) as unknown as RefObject<HTMLElement> // We need to use draggable nodeRef api, otherwise Draggable will use findDOMNode which is deprecated
  const resizeKey = useResizeWindow()
  const { width: elementWidth, height: elementHeight } =
    useResizeObserver(draggableRef, 20) ?? {} // Used to ensure modal doesn't go off screen after content resize
  const [position, setPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const minimumVisiblePx = 100
    const resizeKeySplit = resizeKey.split('-')
    const windowHeight = amountUtils.parseInt(resizeKeySplit[0], 10)
    const windowWidth = amountUtils.parseInt(resizeKeySplit[1], 10)
    const draggableBoundingRect = draggableRef.current?.getBoundingClientRect()

    const modalWidth = draggableBoundingRect?.width ?? 0
    const modalX = draggableBoundingRect?.x ?? 0
    const modalY = draggableBoundingRect?.y ?? 0

    const overFlowingTopAmount = modalY < 0 ? modalY : 0 // Since drag handle is top we don't allow any drag outside of top

    const bottomOverflow = windowHeight - minimumVisiblePx - modalY
    const overFlowingBottomAmount = bottomOverflow < 0 ? bottomOverflow : 0

    const rightSideOverflow = windowWidth - minimumVisiblePx - modalX
    const overFlowingRightAmount = rightSideOverflow < 0 ? rightSideOverflow : 0

    const leftSideOverflow = modalX - (0 - (modalWidth - minimumVisiblePx))
    const overFlowingLeftAmount = leftSideOverflow < 0 ? leftSideOverflow : 0

    if (
      modalWidth > 0 && // Unit tests will have modal width 0
      (overFlowingLeftAmount ||
        overFlowingRightAmount ||
        overFlowingTopAmount ||
        overFlowingBottomAmount)
    ) {
      setPosition((prev) => ({
        ...prev,
        x: position.x - (overFlowingLeftAmount - overFlowingRightAmount),
        y: position.y - (overFlowingTopAmount - overFlowingBottomAmount),
      }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- seems like a false positive in oxlint wanting draggableRef.current in deps
  }, [position, resizeKey, elementWidth, elementHeight])

  const handleDrag = (e: DraggableEvent) => {
    // Needed to ensure only top modal moves on drag when modals are nested
    e.stopPropagation()
  }

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    handleDrag(e)
    setPosition({ x: data.x, y: data.y })
  }

  return (
    <Draggable
      handle="#draggable-handle"
      nodeRef={draggableRef}
      onDrag={handleDrag}
      onStart={handleDrag}
      onStop={handleDragStop}
      position={position}
    >
      <DialogPanel
        ref={draggableRef}
        className={cx(
          'flex flex-col items-stretch rounded-2xl',
          'shadow-xl ring ring-offset-0 ring-amaze-paperDark/10 dark:ring-black/50',
          'min-w-80 max-w-[calc(100vw-var(--spacing)*6)] max-h-[calc(100dvh-var(--spacing)*6)]', // ensure modal never bigger than the screen
          'overflow-hidden', // ensure e.g. rounded corners are respected, we use portals for tooltips so that shouldn't be a problem
          paperStyles(),
          panelClassName,
        )}
        {...htmlProps}
      >
        <ModalProvider onClose={onClose}>{children}</ModalProvider>
      </DialogPanel>
    </Draggable>
  )
}

export const ModalDraggableDialogPanel = memo(
  MemorizedModalDraggableDialogPanel,
)
